export type CronModes = 'hourly' | 'daily' | 'monthly' | 'yearly';

export type CronMode = {
    type?: string;
    value?: any;
    lang_rsc?: any;
};

export interface TaskSchedule {
    TaskId?: string;
    ObjectId?: string;
    CronExpression?: string;
    Scheduled?: string | Date | null;
    TaskType?: string;
    TaskStatus?: string;
    IsRecurring?: boolean;
    TimeZone?: string;
}

export interface RecurringEditorState {
    showEditor: boolean;
    displayInactiveTasks: boolean;
    cronExpression: string;
    nextDates: string[];
    schedules: TaskSchedule[];
    selectedTaskSchedule: TaskSchedule;
    objectType: string;
    objectId: string;
    resendToExistingRecipients: boolean;
}

export type TaskScheduleController = {
    startingStep: number | null;
    Scheduled: Date | string | null;
    TimeZone: string;
    TaskStatus: string | null;
    IsRecurring: boolean;
    CronExpression: string; // initial pattern
    TaskType: string;
    activeTasks: {
        [key: string]: {
            TaskId: string;
            CronExpression: string; // initial + task pattern
            nodeId: number;
        };
    };
};

/** import through func to get the latest $Resource object */
export default () => {
    const modes: Record<CronModes, CronMode[]> = {
        hourly: [
            {
                type: 'hourly',
                lang_rsc: 'By hour',
            },
            {
                type: 'minutes',
                lang_rsc: 'By minute',
            },
        ],
        daily: [
            {
                type: 'day',
                lang_rsc: $Resources.CronEveryDayAtLabel,
            },
            {
                type: 'odd',
                lang_rsc: $Resources.CronEveryOddDayAtLabel,
            },
            {
                type: 'even',
                lang_rsc: $Resources.CronEveryEvenDayAtLabel,
            },
        ],
        monthly: [
            {
                type: 'day',
            },
            {
                type: 'weekday',
            },
        ],
        yearly: [
            {
                type: 'day',
                lang_rsc: $Resources.CronEveryDayAtLabel,
            },
            {
                type: 'weekday',
                lang_rsc: $Resources.CronEveryDayAtLabel,
            },
        ],
    };

    const cronValues: { [k: string]: CronMode[] } = {
        weekdays: [
            {
                value: 'MON',
                lang_rsc: $Resources.CronWeekdayMON,
            },
            {
                value: 'TUE',
                lang_rsc: $Resources.CronWeekdayTUE,
            },
            {
                value: 'WED',
                lang_rsc: $Resources.CronWeekdayWED,
            },
            {
                value: 'THU',
                lang_rsc: $Resources.CronWeekdayTHU,
            },
            {
                value: 'FRI',
                lang_rsc: $Resources.CronWeekdayFRI,
            },
            {
                value: 'SAT',
                lang_rsc: $Resources.CronWeekdaySAT,
            },
            {
                value: 'SUN',
                lang_rsc: $Resources.CronWeekdaySUN,
            },
        ],
        months: [
            {
                value: 'JAN',
                // TODO: yepp, dynamic key parsing will simplify this..
                lang_rsc: $Resources.CronMonthJAN,
            },
            {
                value: 'FEB',
                lang_rsc: $Resources.CronMonthFEB,
            },
            {
                value: 'MAR',
                lang_rsc: $Resources.CronMonthMAR,
            },
            {
                value: 'APR',
                lang_rsc: $Resources.CronMonthAPR,
            },
            {
                value: 'MAY',
                lang_rsc: $Resources.CronMonthMAY,
            },
            {
                value: 'JUN',
                lang_rsc: $Resources.CronMonthJUN,
            },
            {
                value: 'JUL',
                lang_rsc: $Resources.CronMonthJUL,
            },
            {
                value: 'AUG',
                lang_rsc: $Resources.CronMonthAUG,
            },
            {
                value: 'SEP',
                lang_rsc: $Resources.CronMonthSEP,
            },
            {
                value: 'OCT',
                lang_rsc: $Resources.CronMonthOCT,
            },
            {
                value: 'NOV',
                lang_rsc: $Resources.CronMonthNOV,
            },
            {
                value: 'DEC',
                lang_rsc: $Resources.CronMonthDEC,
            },
        ],
        //**** I mean, I generated these, but still can be better
        dayOrdersInMonth: [
            {
                value: '#1',
                lang_rsc: $Resources.CronDayOrderInMonth1,
            },
            {
                value: '#2',
                lang_rsc: $Resources.CronDayOrderInMonth2,
            },
            {
                value: '#3',
                lang_rsc: $Resources.CronDayOrderInMonth3,
            },
            {
                value: '#4',
                lang_rsc: $Resources.CronDayOrderInMonth4,
            },
            {
                value: '#5',
                lang_rsc: $Resources.CronDayOrderInMonth5,
            },
            {
                value: 'L',
                lang_rsc: $Resources.CronDayOrderInMonthL,
            },
        ],
        daysOfMonth: [
            {
                value: 1,
                lang_rsc: $Resources.CronDayInMonth1,
            },
            {
                value: 2,
                lang_rsc: $Resources.CronDayInMonth2,
            },
            {
                value: 3,
                lang_rsc: $Resources.CronDayInMonth3,
            },
            {
                value: 4,
                lang_rsc: $Resources.CronDayInMonth4,
            },
            {
                value: 5,
                lang_rsc: $Resources.CronDayInMonth5,
            },
            {
                value: 6,
                lang_rsc: $Resources.CronDayInMonth6,
            },
            {
                value: 7,
                lang_rsc: $Resources.CronDayInMonth7,
            },
            {
                value: 8,
                lang_rsc: $Resources.CronDayInMonth8,
            },
            {
                value: 9,
                lang_rsc: $Resources.CronDayInMonth9,
            },
            {
                value: 10,
                lang_rsc: $Resources.CronDayInMonth10,
            },
            {
                value: 11,
                lang_rsc: $Resources.CronDayInMonth11,
            },
            {
                value: 12,
                lang_rsc: $Resources.CronDayInMonth12,
            },
            {
                value: 13,
                lang_rsc: $Resources.CronDayInMonth13,
            },
            {
                value: 14,
                lang_rsc: $Resources.CronDayInMonth14,
            },
            {
                value: 15,
                lang_rsc: $Resources.CronDayInMonth15,
            },
            {
                value: 16,
                lang_rsc: $Resources.CronDayInMonth16,
            },
            {
                value: 17,
                lang_rsc: $Resources.CronDayInMonth17,
            },
            {
                value: 18,
                lang_rsc: $Resources.CronDayInMonth18,
            },
            {
                value: 19,
                lang_rsc: $Resources.CronDayInMonth19,
            },
            {
                value: 20,
                lang_rsc: $Resources.CronDayInMonth20,
            },
            {
                value: 21,
                lang_rsc: $Resources.CronDayInMonth21,
            },
            {
                value: 22,
                lang_rsc: $Resources.CronDayInMonth22,
            },
            {
                value: 23,
                lang_rsc: $Resources.CronDayInMonth23,
            },
            {
                value: 24,
                lang_rsc: $Resources.CronDayInMonth24,
            },
            {
                value: 25,
                lang_rsc: $Resources.CronDayInMonth25,
            },
            {
                value: 26,
                lang_rsc: $Resources.CronDayInMonth26,
            },
            {
                value: 27,
                lang_rsc: $Resources.CronDayInMonth27,
            },
            {
                value: 28,
                lang_rsc: $Resources.CronDayInMonth28,
            },
            {
                value: 29,
                lang_rsc: $Resources.CronDayInMonth29,
            },
            {
                value: 30,
                lang_rsc: $Resources.CronDayInMonth30,
            },
            {
                value: 31,
                lang_rsc: $Resources.CronDayInMonth31,
            },
            {
                value: 'L',
                lang_rsc: $Resources.CronDayInMonthL,
            },
            {
                value: '1W',
                lang_rsc: $Resources.CronDayInMonth1W,
            },
            {
                value: '2W',
                lang_rsc: $Resources.CronDayInMonth2W,
            },
            {
                value: '3W',
                lang_rsc: $Resources.CronDayInMonth3W,
            },
            {
                value: '4W',
                lang_rsc: $Resources.CronDayInMonth4W,
            },
            {
                value: '5W',
                lang_rsc: $Resources.CronDayInMonth5W,
            },
            {
                value: '6W',
                lang_rsc: $Resources.CronDayInMonth6W,
            },
            {
                value: '7W',
                lang_rsc: $Resources.CronDayInMonth7W,
            },
            {
                value: '8W',
                lang_rsc: $Resources.CronDayInMonth8W,
            },
            {
                value: '9W',
                lang_rsc: $Resources.CronDayInMonth9W,
            },
            {
                value: '10W',
                lang_rsc: $Resources.CronDayInMonth10W,
            },
            {
                value: '11W',
                lang_rsc: $Resources.CronDayInMonth11W,
            },
            {
                value: '12W',
                lang_rsc: $Resources.CronDayInMonth12W,
            },
            {
                value: '13W',
                lang_rsc: $Resources.CronDayInMonth13W,
            },
            {
                value: '14W',
                lang_rsc: $Resources.CronDayInMonth14W,
            },
            {
                value: '15W',
                lang_rsc: $Resources.CronDayInMonth15W,
            },
            {
                value: '16W',
                lang_rsc: $Resources.CronDayInMonth16W,
            },
            {
                value: '17W',
                lang_rsc: $Resources.CronDayInMonth17W,
            },
            {
                value: '18W',
                lang_rsc: $Resources.CronDayInMonth18W,
            },
            {
                value: '19W',
                lang_rsc: $Resources.CronDayInMonth19W,
            },
            {
                value: '20W',
                lang_rsc: $Resources.CronDayInMonth20W,
            },
            {
                value: '21W',
                lang_rsc: $Resources.CronDayInMonth21W,
            },
            {
                value: '22W',
                lang_rsc: $Resources.CronDayInMonth22W,
            },
            {
                value: '23W',
                lang_rsc: $Resources.CronDayInMonth23W,
            },
            {
                value: '24W',
                lang_rsc: $Resources.CronDayInMonth24W,
            },
            {
                value: '25W',
                lang_rsc: $Resources.CronDayInMonth25W,
            },
            {
                value: '26W',
                lang_rsc: $Resources.CronDayInMonth26W,
            },
            {
                value: '27W',
                lang_rsc: $Resources.CronDayInMonth27W,
            },
            {
                value: '28W',
                lang_rsc: $Resources.CronDayInMonth28W,
            },
            {
                value: '29W',
                lang_rsc: $Resources.CronDayInMonth29W,
            },
            {
                value: '30W',
                lang_rsc: $Resources.CronDayInMonth30W,
            },
            {
                value: 'LW',
                lang_rsc: $Resources.CronDayInMonthLW,
            }
        ],
    };

    return { modes, cronValues };
};
